/**
 * DiagSnapshotReporter (G.2, 2026-05-13) — unifies the 10 previously local-
 * only debug signals into one server-bound push every 5s. Server caches
 * per-session (DiagSnapshotBuffer) and surfaces via `GET /api/diag/snapshot`
 * for the `dj_h2000_diag_snapshot` MCP tool.
 *
 * Closes the gap left by FrameJitterReporter / LatencyReporter /
 * SendRateReporter — those three ship their own focused
 * snapshots; the other ten in-game tools (event-rate, render-count, net-
 * traffic, net-spike, etc.) had no server visibility before this. With
 * this reporter, `/diagnostic` and MCP get a coherent point-in-time view
 * with one fetch.
 *
 * Mirrors the FrameJitterReporter pattern (LRG.27). Gated on
 * isNetobsEnabled() so prod sessions don't push when nothing's reading.
 *
 * Mounted from main.tsx alongside the other four reporters.
 */

import React, { useEffect, useRef } from 'react';
import { isNetobsEnabled } from './isNetobsEnabled';
import { EventBus } from '../../../shared/EventBus';
import { RenderCountTracker } from '../../diagnostics/RenderCountTracker';
import { useColyseusMessages } from '../../ui/hooks/useColyseusMessages';
import type { TickHealthSnapshot } from '../../../shared/diagnostics/TickHealthTypes';
import type {
  DiagSnapshotWire,
  DiagEventBusSnapshot,
  DiagRendersSnapshot,
  DiagNetTrafficSnapshot,
  DiagSpikeSnapshot,
  DiagTickBudgetSnapshot,
  DiagPlayerSnapshot,
} from '../../../shared/diagnostics/DiagSnapshotTypes';

const REPORT_INTERVAL_MS = 5000;
const TOP_EMITTERS = 5;

/** Read the NetSpikeAnalyzer tier from the DOM, same source the in-game
 *  LagRootCauseOverlay uses. Returns undefined if the panel isn't mounted. */
function readSpikeTier(): string | undefined {
  try {
    const el = document.querySelector('[data-testid="net-spike-analyzer"]');
    return el?.getAttribute('data-tier') ?? undefined;
  } catch {
    return undefined;
  }
}

function collectEventBus(): DiagEventBusSnapshot | null {
  try {
    const stats = EventBus.getInstance().getEventStats();
    if (!stats || stats.length === 0) return { topEmitters: [], totalEmitsLastSec: 0 };
    let total = 0;
    for (const s of stats) total += s.emitsLastSec;
    return {
      topEmitters: stats.slice(0, TOP_EMITTERS).map((s) => ({
        name: s.name,
        emitsLastSec: s.emitsLastSec,
      })),
      totalEmitsLastSec: total,
    };
  } catch {
    return null;
  }
}

function collectRenders(): DiagRendersSnapshot | null {
  try {
    const t = RenderCountTracker.getTotals();
    return {
      frameCount: t.frameCount,
      totalDoubleRenderFrames: t.totalDoubleRenderFrames,
      totalDoubleRenderEvents: t.totalDoubleRenderEvents,
    };
  } catch {
    return null;
  }
}

function collectNetTraffic(): DiagNetTrafficSnapshot | null {
  try {
    const nm = (globalThis as any).nm;
    const tracer = nm?.tracer;
    if (!tracer) return null;
    // Find the most-frequent outbound type in the rolling stats map.
    let topType: string | undefined;
    let topCount = 0;
    const outbound: Map<string, { count: number }> | undefined = tracer.outboundMessageTypeStats;
    if (outbound && typeof outbound.entries === 'function') {
      for (const [type, stats] of outbound.entries()) {
        if (stats.count > topCount) {
          topCount = stats.count;
          topType = type;
        }
      }
    }
    return {
      messagesSent: tracer.messagesSent ?? 0,
      messagesReceived: tracer.messagesReceived ?? 0,
      currentThroughput: tracer.currentThroughput ?? 0,
      trafficEventsRingSize: Array.isArray(tracer.recentTraffic) ? tracer.recentTraffic.length : 0,
      topOutboundType: topType ? { type: topType, count: topCount } : undefined,
    };
  } catch {
    return null;
  }
}

function collectPlayer(): DiagPlayerSnapshot | null {
  try {
    const nm = (globalThis as any).nm;
    const room = nm?.getRoom?.();
    const sessionId = room?.sessionId;
    if (!room || !sessionId) return null;
    const player = room.state?.players?.get?.(sessionId);
    if (!player || typeof player.x !== 'number' || typeof player.y !== 'number') return null;
    return { x: Math.round(player.x), y: Math.round(player.y) };
  } catch {
    return null;
  }
}

const DiagSnapshotReporterInner: React.FC = () => {
  // Cache the most-recent TickHealthSnapshot. Read at push time. useRef
  // avoids re-renders (component returns null).
  const tickHealthRef = useRef<TickHealthSnapshot | null>(null);

  useColyseusMessages({
    'netobs:tick': (snap: TickHealthSnapshot) => {
      tickHealthRef.current = snap;
    },
  });

  useEffect(() => {
    const sendSnapshot = (): void => {
      const room = (globalThis as any).nm?.getRoom?.();
      if (!room || typeof room.send !== 'function') return;
      try {
        const spikeTier = readSpikeTier();
        const spike: DiagSpikeSnapshot | null = spikeTier ? { tier: spikeTier } : null;
        const tick = tickHealthRef.current;
        let tickBudget: DiagTickBudgetSnapshot | null = null;
        if (tick) {
          // Severity derived client-side from the rolling tick budget.
          // budgetMs is the per-tick target; meanMs is observed wall-time.
          // > budget → severe (overrunning), > 0.7×budget → mild, else healthy.
          let severity: 'healthy' | 'mild' | 'severe' = 'healthy';
          if (tick.budgetMs > 0) {
            const ratio = tick.meanMs / tick.budgetMs;
            if (ratio > 1.0) severity = 'severe';
            else if (ratio > 0.7) severity = 'mild';
          }
          tickBudget = { rollingBudgetMs: tick.budgetMs, severity };
        }
        const snapshot: DiagSnapshotWire = {
          capturedAt: Date.now(),
          eventBus: collectEventBus(),
          renders: collectRenders(),
          netTraffic: collectNetTraffic(),
          player: collectPlayer(),
          spike,
          tickBudget,
        };
        room.send('netobs:diagSnapshot', snapshot);
      } catch {
        // diagnostic-only, never gameplay
      }
    };
    const id = setInterval(sendSnapshot, REPORT_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return null;
};

export const DiagSnapshotReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <DiagSnapshotReporterInner />;
};
